import { logger } from '@/app/logger';
import { publicGet } from './api';
import { isArticleDetailPublicPath } from './isArticleDetailPublicPath';

const log = logger.create('site:publicContentCache');

interface CacheEntry<T> {
  promise: Promise<T>;
  data?: T;
}

const cache = new Map<string, CacheEntry<unknown>>();

export function peekPublicContent<T>(path: string): T | undefined {
  return cache.get(path)?.data as T | undefined;
}

export function getPublicContent<T>(path: string): Promise<T> {
  const existing = cache.get(path);
  const alwaysRevalidate = isArticleDetailPublicPath(path);

  if (!alwaysRevalidate && existing?.data !== undefined) {
    return Promise.resolve(existing.data as T);
  }
  if (!alwaysRevalidate && existing?.promise) {
    return existing.promise as Promise<T>;
  }

  log.debug('getPublicContent', 'Fetching public content', { path, alwaysRevalidate });

  const promise = publicGet<T>(path)
    .then((data) => {
      const entry = cache.get(path);
      if (entry) {
        entry.data = data;
      } else {
        cache.set(path, { promise: Promise.resolve(data), data });
      }
      log.debug('getPublicContent', 'Public content cached', { path, alwaysRevalidate });
      return data;
    })
    .catch((err) => {
      cache.delete(path);
      const message = err instanceof Error ? err.message : String(err);
      log.warn('getPublicContent', 'Public content fetch failed', { path, message });
      throw err;
    });

  cache.set(path, { promise, data: existing?.data });
  return promise;
}

export function prefetchPublicContent(path: string): void {
  getPublicContent(path).catch(() => {
    // Background prefetch — errors are logged in getPublicContent.
  });
}

export function prefetchArticleBySlug(slug: string): void {
  prefetchPublicContent(`/api/articles/${slug}`);
}

function prefetchOnHover(path: string): {
  onMouseEnter: () => void;
  onFocus: () => void;
} {
  return {
    onMouseEnter: () => prefetchPublicContent(path),
    onFocus: () => prefetchPublicContent(path),
  };
}
